import { TransferTicketBody } from './types';

/**
 * 결과를 모르는 양도(응답 유실·자동 재확인 상한 도달·STALE·상품 분류 적용 겹침 보류)를 원본 수강권 기준으로 10분간 기억한다.
 * 창을 닫았다 다시 열어도 같은 원본이면 **같은 키·같은 본문**으로만 다시 확인하게 한다
 * (새 키로 보내면 첫 요청이 처리됐을 때 두 번째 양도가 된다).
 *
 * 10분이 지나면 키는 버리지만 조용히 풀지 않는다 — 그 원본에 「수강권 목록을 확인했어요」 체크 요구(24시간)를 남긴다
 * (최종 검증 4회차 지적 1: 만료 뒤 새 키가 바로 허용되면 앞 요청이 남긴 수강권 위에 두 번째 양도가 된다).
 *
 * sessionStorage 를 쓰고, 같은 내용을 **페이지 수명 동안 메모리에도** 둔다. 저장소 접근이 막히거나(사생활 보호 모드 등)
 * 쓰기가 실패해도 이 페이지 안에서는 메모리로 판정한다(4회차 지적 2).
 */
export type StoredPendingTransfer<T extends { body: TransferTicketBody }> = T & { savedAt: number };

export const PENDING_TRANSFER_TTL_MS = 10 * 60 * 1000;
const PREFIX = 'rappo:ticketTransfer:pending:';

const storage = (): Storage | null => {
    try {
        return typeof window !== 'undefined' ? window.sessionStorage : null;
    } catch {
        return null;
    }
};

/**
 * 저장소가 실패해도 남는 페이지 수명 메모리. 키는 저장소와 같은 문자열.
 * 메모리가 저장소보다 우선한다 — 저장소에서 읽은 값도 메모리에 복사하고, 지운 것은 「지움」 표식으로 남겨
 * 저장소 삭제가 실패해도 옛 값이 되살아나지 않게 한다(5회차 지적 2). 메모리에 없을 때만 저장소를 본다.
 */
const TOMBSTONE = Symbol('removed');
const memory = new Map<string, string | typeof TOMBSTONE>();

const writeRaw = (key: string, value: string) => {
    memory.set(key, value);
    const store = storage();
    if (!store) return;
    try {
        store.setItem(key, value);
    } catch {
        // 메모리에 남았다
    }
};

/** 메모리 값(지움 표식이면 없음)이 우선, 메모리에 없으면 저장소 값을 읽어 메모리에 복사한다. */
const readRaw = (key: string): string | null => {
    if (memory.has(key)) {
        const value = memory.get(key);
        return value === TOMBSTONE || value === undefined ? null : value;
    }
    const store = storage();
    if (!store) return null;
    try {
        const value = store.getItem(key);
        if (value !== null) memory.set(key, value);
        return value;
    } catch {
        return null;
    }
};

const removeRaw = (key: string) => {
    memory.set(key, TOMBSTONE);
    const store = storage();
    if (!store) return;
    try {
        store.removeItem(key);
    } catch {
        // 무시
    }
};

/** 테스트용: 페이지 메모리를 비운다. */
export const __resetTransferMemoryForTest = () => memory.clear();

export const savePendingTransfer = <T extends { body: TransferTicketBody }>(attempt: T, now: number = Date.now()) => {
    if (!attempt?.body?.sourceId || !attempt.body.clientOperationId) return;
    writeRaw(PREFIX + attempt.body.sourceId, JSON.stringify({ ...attempt, savedAt: now }));
};

export const loadPendingTransfer = <T extends { body: TransferTicketBody }>(
    sourceId: string,
    now: number = Date.now(),
): StoredPendingTransfer<T> | null => {
    if (!sourceId) return null;
    const raw = readRaw(PREFIX + sourceId);
    if (!raw) return null;
    let parsed: StoredPendingTransfer<T> | null = null;
    try {
        parsed = JSON.parse(raw) as StoredPendingTransfer<T>;
    } catch {
        parsed = null;
    }
    const wellFormed =
        !!parsed &&
        typeof parsed.savedAt === 'number' &&
        parsed.body?.sourceId === sourceId &&
        typeof parsed.body?.clientOperationId === 'string';
    if (!wellFormed) {
        removeRaw(PREFIX + sourceId);
        return null;
    }
    const age = now - (parsed as StoredPendingTransfer<T>).savedAt;
    if (age >= 0 && age < PENDING_TRANSFER_TTL_MS) return parsed;
    // 10분이 지났다(또는 시계가 뒤로 갔다). 결과를 모르는 요청이 있었던 원본이다 — 키는 버리되 사람 확인을 요구한다.
    removeRaw(PREFIX + sourceId);
    saveTransferReview(sourceId, 'unconfirmed', now, (parsed as StoredPendingTransfer<T>).body.clientOperationId);
    return null;
};

export const clearPendingTransfer = (sourceId: string) => {
    if (!sourceId) return;
    removeRaw(PREFIX + sourceId);
};

/** 지금 저장된 대기 기억의 요청 키(없거나 읽을 수 없으면 null). 만료 판정은 하지 않는다. */
const storedPendingKey = (sourceId: string): string | null => {
    const raw = readRaw(PREFIX + sourceId);
    if (!raw) return null;
    try {
        const key = (JSON.parse(raw) as { body?: { clientOperationId?: unknown } })?.body?.clientOperationId;
        return typeof key === 'string' ? key : null;
    } catch {
        return null;
    }
};

/**
 * 요청 결과로 대기 기억을 지운다 — 저장된 기억의 키가 그 요청 키와 같을 때만(7회차 — 확인 요구와 같은 방식).
 * 닫힌 창에 늦게 도착한 옛 요청 K1 의 응답이 같은 원본의 새 요청 K2 대기 기억을 지우면 안 된다. 키 없는 요청은 기억을 만들지 않으므로 아무것도 안 한다.
 */
export const clearPendingTransferForOperation = (sourceId: string, operationKey: string | undefined): boolean => {
    if (!sourceId || !operationKey) return false;
    if (storedPendingKey(sourceId) !== operationKey) return false;
    removeRaw(PREFIX + sourceId);
    return true;
};

/** 저장된 대기 기억이 같은 요청 키일 때만 덮어쓴다(닫힌 창의 늦은 응답이 다른 요청의 기억을 바꾸지 않게). */
export const updatePendingTransferForOperation = <T extends { body: TransferTicketBody }>(
    attempt: T,
    now: number = Date.now(),
): boolean => {
    const key = attempt?.body?.clientOperationId;
    if (!attempt?.body?.sourceId || !key) return false;
    if (storedPendingKey(attempt.body.sourceId) !== key) return false;
    savePendingTransfer(attempt, now);
    return true;
};

/**
 * 「수강권 목록 확인」이 필요한 원본(최종 검증 P1-a·4회차 지적 1).
 * - 'unknown': 실패 재생인데 서버가 되돌리기 결과를 알려 주지 않았다(지금 배포 중인 서버·옛 기록).
 * - 'incomplete': rollbackComplete false — 깨끗하다고 증명하지 못했다(새 수강권이 남았을 수 있다).
 * - 'unconfirmed': 결과를 모르는 요청(응답 유실·겹침 보류 등)의 같은 키 기억이 10분 지나 만료됐다.
 * 사용자가 「회원의 수강권 목록을 확인했어요」를 직접 체크해야 새 키로 다시 보낼 수 있다. 서버도 모르는 상태라 사람이 목록을 보는 수밖에 없다.
 * **시간으로 사라지지 않는다**(5회차 지적 1 — 시간이 지났다고 남은 수강권이 정리되지는 않는다). 체크하고 보내거나,
 * 같은 키 재확인으로 결과(성공·rollbackComplete true)를 받을 때만 지운다. 저장소가 sessionStorage 라 탭을 닫으면 사라진다.
 * 기록에는 그 확인 요구를 만든 요청 키(operationKey)를 함께 남긴다. 결과로 해제할 때는 **응답의 키가 같을 때만** 지운다
 * (6회차 지적 1 — 닫힌 창에 늦게 도착한 옛 요청 K1 의 성공이 새 요청 K2 의 확인 요구를 지우면 안 된다).
 */
export type TransferReviewKind = 'unknown' | 'incomplete' | 'unconfirmed';
const REVIEW_PREFIX = 'rappo:ticketTransfer:review:';
const REVIEW_KINDS: TransferReviewKind[] = ['unknown', 'incomplete', 'unconfirmed'];

export const saveTransferReview = (
    sourceId: string,
    kind: TransferReviewKind,
    now: number = Date.now(),
    operationKey?: string,
) => {
    if (!sourceId) return;
    writeRaw(
        REVIEW_PREFIX + sourceId,
        JSON.stringify({ kind, savedAt: now, ...(operationKey ? { operationKey } : {}) }),
    );
};

/**
 * 요청 결과(성공·깨끗한 실패)로 확인 요구를 푼다. 기록을 만든 요청 키와 응답의 키가 같을 때만 지운다.
 * 키가 없는 기록(옛 기록)이나 다른 요청의 기록은 건드리지 않는다 — 그때는 사용자가 체크하고 보내야 풀린다. 지웠으면 true.
 */
export const clearTransferReviewForOperation = (sourceId: string, operationKey: string | undefined): boolean => {
    if (!sourceId || !operationKey) return false;
    const raw = readRaw(REVIEW_PREFIX + sourceId);
    if (!raw) return false;
    try {
        const parsed = JSON.parse(raw) as { operationKey?: unknown };
        if (parsed?.operationKey !== operationKey) return false;
    } catch {
        return false;
    }
    removeRaw(REVIEW_PREFIX + sourceId);
    return true;
};

export const loadTransferReview = (sourceId: string, now: number = Date.now()): TransferReviewKind | null => {
    if (!sourceId) return null;
    // 만료된 같은 키 기억이 있으면 먼저 확인 요구로 바꾼다(창을 다시 열자마자 고른 원본도 걸리게).
    loadPendingTransfer(sourceId, now);
    const raw = readRaw(REVIEW_PREFIX + sourceId);
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw) as { kind?: unknown; savedAt?: unknown };
        const valid = REVIEW_KINDS.includes(parsed?.kind as TransferReviewKind);
        if (!valid) {
            removeRaw(REVIEW_PREFIX + sourceId);
            return null;
        }
        return parsed.kind as TransferReviewKind;
    } catch {
        removeRaw(REVIEW_PREFIX + sourceId);
        return null;
    }
};

export const clearTransferReview = (sourceId: string) => {
    if (!sourceId) return;
    removeRaw(REVIEW_PREFIX + sourceId);
};
